import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

/**
 * The site's two endless-loop primitives, both CSS-driven. They replace four `motion/react` belts
 * that wrote a `MotionValue` every frame; the trade is that a CSS keyframe cannot be scrubbed, so the
 * wheel and the columns are no longer grab-to-spin.
 */

/* ---------------------------------------------------------------------------------------------- */
/* Belt — a straight line of items looping forever                                                  */
/* ---------------------------------------------------------------------------------------------- */

/**
 * A marquee in either axis. Children are rendered twice and the track slides one copy.
 *
 * Every item must carry its own trailing gap (`mr-*`/`mb-*`, not `gap-*` on the track), or the
 * doubled track is one gap short of two copies and the belt jumps each lap.
 */
export function Belt({
	axis = 'x',
	speed,
	reverse,
	className,
	children,
}: {
	axis?: 'x' | 'y';
	/** px per second, independent of how many items the belt carries. */
	speed: number;
	reverse?: boolean;
	className?: string;
	children: ReactNode;
}) {
	const trackRef = useRef<HTMLDivElement>(null);
	const copyRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const track = trackRef.current;
		const copy = copyRef.current;
		if (!track || !copy) return;
		const update = () => {
			// A flex container's own box sums its items' margins, which is the gap the loop needs.
			const length = axis === 'x' ? copy.offsetWidth : copy.offsetHeight;
			track.style.animationDuration = `${Math.max(1, Math.round(length / speed))}s`;
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(copy);
		return () => ro.disconnect();
	}, [axis, speed]);

	const copyClass = cn('flex shrink-0', axis === 'y' && 'flex-col');

	return (
		<div
			ref={trackRef}
			className={cn('new-belt-item flex', axis === 'x' ? 'w-max' : 'flex-col', className)}
			// Inline LONGHANDS, the same way the wheel declares its animation, and for the same reason:
			// an `animate-[…]` utility is the `animation` SHORTHAND, so it resets every longhand it does
			// not mention. That silently defeated `reverse` — the shorthand put `animation-direction`
			// back to `normal` and both tech-partner columns drifted the same way. It would have defeated
			// the reduced-motion rule too. Duration is deliberately absent here: the layout effect owns
			// it, and `.new-belt-item` in theme.css carries the placeholder the server-rendered markup
			// runs at until then.
			style={{
				animationName: axis === 'x' ? 'new-belt-x' : 'new-belt-y',
				animationTimingFunction: 'linear',
				animationIterationCount: 'infinite',
				animationDirection: reverse ? 'reverse' : 'normal',
			}}
		>
			<div ref={copyRef} className={copyClass}>
				{children}
			</div>
			<div className={copyClass}>{children}</div>
		</div>
	);
}

/* ---------------------------------------------------------------------------------------------- */
/* Wheel — a closed loop up one side of the section and down the other                              */
/* ---------------------------------------------------------------------------------------------- */

// Wheel geometry, shared by the layout and the generated keyframes.
const COL_H = 576; // px — wheel viewport height
const CARD_W = 224; // px — card width (w-56)
const CARD_H = 96; // px — card height (h-24)
const SLOT_H = 112; // px — spacing between cards along the belt (COL_H / SLOT_H ≈ cards per run)
const ARC_AMPLITUDE = 96; // px the middle card bulges toward the center
const MIN_SCALE = 0.78; // scale of a card entering/leaving at top/bottom
const MAX_SCALE = 1.08; // scale of the card sitting at the arc's apex (center)
const MIN_OPACITY = 0.55; // opacity of a card at the far end of a run — depth, not "switched off"
const WHEEL_SPEED = 36; // px per second of belt travel
const RUN_INSET_RATIO = 0.11; // pull both runs this fraction of the width in from the edges
const MIN_ITEMS = 20; // fewest cards that fill both runs, so the two handoffs stay off-screen
/** Keyframe stops sampled across each run's on-screen stretch, where the arc is a curve. */
const ARC_SAMPLES = 18;

/**
 * A card's position at animation progress `s`: the left run travels up over [0, 0.5], the right run
 * down over [0.5, 1]. Each run is half the belt, far taller than the viewport, so both handoffs
 * happen off-screen and the loop reads as one wheel rather than two columns.
 */
function wheelState(s: number, count: number) {
	const L = count * SLOT_H;
	const runLen = L / 2;
	const bp = L * (1 - s);
	const left = bp >= runLen;
	const yc = left ? COL_H / 2 - runLen / 2 + (bp - runLen) : COL_H / 2 - bp + runLen / 2;

	const u = Math.min(1, Math.max(0, yc / COL_H));
	const arc = Math.sin(Math.PI * u);
	const tuck = (1 - arc) * ARC_AMPLITUDE;
	// Percentages resolve against the full-width rail the card rides on, i.e. the wheel's width.
	const inset = RUN_INSET_RATIO * 100;
	const x = left ? `calc(${inset}% + ${tuck.toFixed(2)}px)` : `calc(${100 - inset}% - ${(CARD_W + tuck).toFixed(2)}px)`;

	return {
		x,
		y: yc - CARD_H / 2,
		scale: MIN_SCALE + arc * (MAX_SCALE - MIN_SCALE),
		opacity: MIN_OPACITY + arc * (1 - MIN_OPACITY),
	};
}

/**
 * The wheel's two `@keyframes`, generated per belt length: the ratio between a run and the viewport
 * decides what fraction of a lap the arc occupies, so they cannot be static.
 */
const at = (s: number) => `${(s * 100).toFixed(4)}%`;

function wheelKeyframes(count: number) {
	const L = count * SLOT_H;
	const runLen = L / 2;
	const sOf = (bp: number) => 1 - bp / L;
	const bpLeft = (yc: number) => yc - COL_H / 2 + runLen / 2 + runLen;
	const bpRight = (yc: number) => COL_H / 2 + runLen / 2 - yc;

	const stops = new Set([0, 0.5, 1]);
	for (const bpOf of [bpLeft, bpRight]) {
		const ends = [sOf(bpOf(-CARD_H)), sOf(bpOf(COL_H + CARD_H))];
		const lo = Math.max(0, Math.min(...ends));
		const hi = Math.min(1, Math.max(...ends));
		for (let k = 0; k <= ARC_SAMPLES; k++) stops.add(lo + ((hi - lo) * k) / ARC_SAMPLES);
	}

	const sorted = [...stops].sort((a, b) => a - b);
	const name = `new-wheel-${count}`;
	const rail = sorted.map((s) => {
		const g = wheelState(s, count);
		return `${at(s)}{transform:translate3d(${g.x},${g.y.toFixed(2)}px,0)}`;
	});
	const card = sorted.map((s) => {
		const g = wheelState(s, count);
		return `${at(s)}{scale:${g.scale.toFixed(4)};opacity:${g.opacity.toFixed(4)}}`;
	});

	return {
		name,
		duration: L / WHEEL_SPEED,
		css: `@keyframes ${name}-rail{${rail.join('')}}@keyframes ${name}-card{${card.join('')}}`,
	};
}

/**
 * Repeat the list until it overflows both runs, so the handoffs stay off-screen, then interleave:
 * the runs sit half a belt apart, so feeding even ranks to one and odd to the other puts the top of
 * the ranking on both sides instead of the bottom half on one.
 */
function beltOrder<T>(items: T[]): T[] {
	if (items.length === 0) return [];
	const filled =
		items.length < MIN_ITEMS
			? Array.from({ length: items.length * Math.ceil(MIN_ITEMS / items.length) }, (_, k) => items[k % items.length]!)
			: items;
	const half = Math.ceil(filled.length / 2);
	return filled.map((_, i) => filled[i < half ? i * 2 : (i - half) * 2 + 1] ?? filled[i]!);
}

/**
 * The ferris wheel: one belt down the right of the section and up the left. Every card runs the same
 * keyframes and differs only in its negative `animation-delay`, so no JavaScript runs after mount.
 */
export function Wheel<T>({
	items,
	renderItem,
	className,
}: {
	items: T[];
	renderItem: (item: T, i: number) => ReactNode;
	className?: string;
}) {
	const belt = beltOrder(items);
	const count = belt.length;
	if (count === 0) return <div className={cn('h-[576px]', className)} />;

	const { name, duration, css } = wheelKeyframes(count);
	const L = count * SLOT_H;

	return (
		<div
			className={cn(
				'relative h-[576px] w-full overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_14%,black_86%,transparent)] select-none',
				className,
			)}
		>
			<style href={name} precedence="medium" dangerouslySetInnerHTML={{ __html: css }} />
			{belt.map((item, i) => {
				// A quarter turn in, which opens on item 0 at the right apex and item 1 at the left.
				const progress = 1 - ((((i * SLOT_H + L / 4) % L) + L) % L) / L;
				// Longhands: the shorthand would set `animation-play-state: running` inline and beat the
				// reduced-motion rule in theme.css.
				const timing = {
					animationDuration: `${duration.toFixed(3)}s`,
					animationTimingFunction: 'linear',
					animationDelay: `${(-progress * duration).toFixed(3)}s`,
					animationIterationCount: 'infinite',
				} as const;
				return (
					<div
						// oxlint-disable-next-line react/no-array-index-key
						key={i}
						className="new-belt-item absolute top-0 left-0 h-0 w-full"
						style={{ animationName: `${name}-rail`, ...timing }}
					>
						{/* `w-fit`: `scale` pivots on the element's own centre, so a full-width box drifts sideways. */}
						<div className="new-belt-item w-fit" style={{ animationName: `${name}-card`, ...timing }}>
							{renderItem(item, i)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
